import $ from '@modules/jquery-import';
import assign from 'object-assign';
import appLogger from '@core/LoggingProvider';
import Class from '@core/Class';
import Module from '@core/Module';
import View from '@core/View';
import User from '../User';
import OrdersService from '@services/OrdersService';
import {jqueryScrolling, jqueryTogglable} from '@modules/lazy-imports';

const
    ORDER_DETAIL_LOAD_ERROR = `Sorry, we couldn't retrieve the details for this order, try viewing the <a href="[orderDetailUrl]">full order details here</a>`,
    PO_UPDATE_ERROR_MSG = `There was a problem while attempting to update this PO#. This field has been set to it's previous value.`;

/**
 * OrderItemController
 * Common Controller for managing order item
 * Handles basic interactions such as requesting full order details, print invoice and target scrolling behaviors
 * @el {HTMLElement} - the order item container
 */
const OrderItemController = (Class.extend( Module )).extend( {

    defaults: {
        request: OrdersService.getOrderDetailsHtml,
        orderDetailsUrl: upi.paths.ORDER_DETAILS_URL
    },

    events: {
        'click .print-invoice': 'handlePrintInvoice',
        'contenteditable:saved': 'handlePONumber'
    },

    elements: {
        '.accordion-content': 'contentArea',
        '.odr-po': 'pofieldWrapper',
        '.odr-po input': 'pofield'
    },

    init: function ( el, options ) {

        this.options = this.options || {};
        assign( this.options, this.defaults, options || {} );

        if ( !this.options.request ) {
            throw Error( 'upi.OrderItemController requires a "request" param for webservice requests' );
        }

        this.el = $( el );
        this.orderGuid = this.el.data( 'order-guid' );
        this.orderNumber = this.el.data( 'order-number' );
        this.messageBoxView = new View( 'messagebox' );
        this.currentStatus = this.el.data( 'order-status' );
        this.el.parents( '.accordion' ).on( 'close:accordion open:accordion', this.handleAccordionToggle.bind( this ) );
        this.el.parents( '.accordion' ).on( 'open:accordion', this.loadOrderDetails.bind( this ) );

        jqueryTogglable().then( () => this.el.togglable() );

        this.update_elements();

        // ensure dialog pofield gets updated when contenteditable field changes
        this.bind( 'pofield:update', ( e, {orderNumber, poNumber} ) => {
            if ( orderNumber === this.orderNumber ) {
                this.pofieldWrapper.find( 'Togglable-value' ).text( poNumber );
                this.pofieldWrapper.find( 'input' ).val( poNumber );
            }
        } );
    },

    redirectToLogin: function () {
        this.setLoadMessage( 'redirecting', upi.conf.SESSION_TIMEOUT );
        window.setTimeout( User.redirectToLogin, 750 );
    },

    handleAccordionToggle: function ( e ) {
        var action = e.type.split( ':' )[0];
        if ( this.isRootEl( e.item ) && 'open' === action ) {
            this.scrollToOrderItem();
        }
    },

    handlePONumber: function ( e, data ) {
        const $el = $( e.target ),
            content = data.contents;

        if ( content && content.toString().length > 0 ) {
            this.updatePONumber( content )
                .catch( err => {
                    appLogger.capture( err );
                    console.error( err );
                    data.contenteditable.resetField();
                    $el.showHint( {type: 'error', message: PO_UPDATE_ERROR_MSG} );
                } );
        }
    },

    isRootEl: function ( target ) {
        return this.el.get( 0 ) === target.get( 0 );
    },

    setLoadMessage: function ( type, message ) {
        var messageView = this.messageBoxView.render( {
            type: type || '',
            effect: 'slideDown',
            message: message || ''
        } );
        this.contentArea.html( messageView );
    },

    loadOrderDetails: function ( e ) {
        if ( this.isRootEl( e.item ) && !this.el.data( 'content-loaded' ) ) {
            this.setLoadMessage( 'processing', 'Loading...' );

            this.getOrderDetails( this.orderNumber )
                .then( $details => this.renderOrderPartial( $details ) )
                .catch( error => {
                    appLogger.capture( error );
                    const params = this.currentStatus === 'pending' ?
                        {orderGuid: this.orderGuid} :
                        {orderNumber: this.orderNumber};
                    this.setLoadMessage( 'error', this.getLoadErrorMessage( params ) );
                } )
        }
    },

    renderOrderPartial: function ( $invoice ) {
        // extract the invoice and order actions from page markup
        var invoiceHTML = $( '<div class="slideDown"/>' )
            .html( $( $invoice ).find( '.odr-line-list, .odr-line-summary, .odr-actions' ) );
        this.contentArea.html( invoiceHTML );
        this.el.data( 'content-loaded', true );
        this.update_elements();
    },

    handlePrintInvoice: function ( e ) {
        e.preventDefault();
        e.stopPropagation();
        const $target = $( e.target );

        $( e.currentTarget ).toggleClass( 'processing' );

        this.getOrderDetails( this.orderNumber )
            .then(
                this.printInvoiceHTML,
                () => window.print()
            )
            .then( () => $target.toggleClass( 'processing' ) );
    },

    printInvoiceHTML: function ( $invoice ) {
        // prepare the markup to print a single invoice
        // only trigger print-dialog if the invoice wrapper is found, this will fallback to default view
        var $printDialog = $( '#print-invoice-dialog' );

        if ( !$printDialog[0] ) {
            $printDialog = $( '<div/>', {id: 'print-invoice-dialog'} ).appendTo( 'body' );
        }

        $( 'html' ).toggleClass( 'print-invoice', !!$invoice[0] );
        $printDialog.html( $invoice );

        window.print();

        // clear dialog
        window.setTimeout( function () {
            // note: do not cache $('html') intentionally
            $( 'html' ).removeClass( 'print-invoice' );
            $printDialog.html( '' );
        }, 500 );
    },

    /**
     * Get order details section from the order details page for specified order
     * @param orderNumber
     * @returns promise
     */
    getOrderDetails: function ( orderNumber ) {
        var {request} = this.options;

        return Promise.resolve()
            .then( () => {
                if ( typeof request !== 'function' ) {
                    throw new Error( 'getOrderDetailsFor: missing required keys to get order info "request" option' );
                }
            } ).then( () => {
                return request( {orderNumber} )
                    .then( pageHtml => {
                        const $invoice = $( pageHtml );
                        // sanity check to ensure we received actual HTML
                        if ( !$invoice.get( 0 ) ) {
                            throw new Error( 'Invoice was not returned' );
                        }
                        return $invoice;
                    } )
                    .catch( error => {
                        appLogger.capture( error );
                        console.error( '[OrderItemController]', error );

                        let reason = error.reason;
                        switch ( reason ) {
                            case 'noauth':
                                this.redirectToLogin();
                                return Promise.reject();

                            default:
                                throw error;
                        }
                    } );
            } )

    },

    getLoadErrorMessage: function ( params = {} ) {
        const searchParams = new URLSearchParams( params );
        return ORDER_DETAIL_LOAD_ERROR.replace(
            /\[orderDetailUrl\]/,
            `${upi.paths.ORDER_DETAILS_URL}?${searchParams.toString()}`
        );
    },

    updatePONumber: function ( val ) {
        const poNumber = val.toString().trim();
        const {orderNumber} = this;
        const data = {poNumber, orderNumber};

        return OrdersService.updateOrderPO( data )
            .then( () => {
                this.trigger( 'pofield:update', data );
                return data.poNumber;
            } )
    },

    removePOField: function () {
        this.pofieldWrapper.html( 'PO# <span>' + this.pofield.val() + '</span>' );
    },

    validatePONumber: function () {
        return this.pofield.valid();
    },

    destroy: function () {
        this._super();
        this.el.off( 'contenteditable:saved' );
    },

    /** Helpers **/
    scrollToOrderItem: function () {
        jqueryScrolling().then( () => this.el.scrollToElement() );
    }

} );

export default OrderItemController;
